import { Stack } from "@mui/material";
import { LinearProgressWithLabel } from "components/LinearProgressWithLabel";
import { useTranslation } from "react-i18next";
import { cn } from "utils/styling";

import type { IfcScheduleProgress } from "../../model/types";

import { SCHEDULE_ENTITY_CONFIG } from "../../lib/configs";
import {
  fillScheduleProgressEmptyValues,
  getEntityPercent,
  hasScheduleProgress,
  isPhaseActive,
} from "../../lib/scheduleProgress";

export const ScheduleProgressSection = ({ progress }: { progress: IfcScheduleProgress }) => {
  const { t } = useTranslation();

  if (!hasScheduleProgress(progress)) return null;

  const phase = progress.phase;
  const filledProgress = fillScheduleProgressEmptyValues(progress);

  return (
    <section className="pt-6 border-t-ds-subtle">
      <h3 className="font-ds-body-semibold-md">{t("Execution.steps.scheduleProgress.title")}</h3>
      {phase && (
        <p className="mt-2 text-lds-foreground-secondary font-ds-body-regular-xs">
          {t("Execution.steps.scheduleProgress.phaseLabel")}:{" "}
          <span className="font-medium ">{t(`Execution.steps.scheduleProgress.phase.${phase}`)}</span>
        </p>
      )}
      <Stack className="mt-4 gap-4">
        {SCHEDULE_ENTITY_CONFIG.map((config) => {
          const total = filledProgress[config.totalKey];
          if (total == null) return null;

          const inserted = filledProgress[config.insertedKey];
          const percent = getEntityPercent(inserted, total);
          const active = isPhaseActive(phase, config.phase);

          return (
            <div
              className={cn(
                "rounded-lg border p-4",
                active ? "border-lds-border-accent bg-lds-selection-subtle" : "bg-lds-surface-bright border-ds-subtle"
              )}
              key={config.entity}
            >
              <Stack className="mb-2 flex-row items-center justify-between gap-2">
                <span className="font-medium font-ds-body-semibold-sm ">
                  {t(`Execution.steps.scheduleProgress.entities.${config.entity}`)}
                </span>
                <span className="text-lds-foreground-secondary font-ds-body-regular-xs">
                  {t("Execution.steps.scheduleProgress.count", { inserted, total })}
                </span>
              </Stack>
              <LinearProgressWithLabel fullWidth hasAnimation={active} value={percent} />
              <span className="mt-1 block text-lds-foreground-muted font-ds-body-regular-xxs">
                {t("Execution.steps.scheduleProgress.percent", { value: percent })}
              </span>
            </div>
          );
        })}
      </Stack>
    </section>
  );
};
